import React, { useRef, useState } from 'react';
import { Button, Menu, MenuLabel, RangeCalendar, Sym, isoLocal } from '../../../components/app2';

/**
 * Selector del periodo a descargar del SAT (rediseño jul-2026).
 *
 * El diseño propone un botón con el rango y un desplegable con "periodos
 * frecuentes" más un rango personalizado. Eso es puro azúcar de interacción
 * sobre el mismo estado de siempre (`{ startDate, endDate }` en formato
 * YYYY-MM-DD): los atajos solo calculan esas dos fechas.
 *
 * El rango a medida usa el calendario del sistema (RangeCalendar). Antes eran
 * dos campos de fecha nativos: cada navegador los pintaba a su manera —en Mac,
 * un calendario gris que no se parecía en nada al resto de la app— y obligaban
 * a elegir las dos fechas por separado, sin ver el rango.
 */

// Fecha LOCAL: toISOString pasa a UTC y, según la zona del navegador, devolvía
// el día anterior (un periodo "1 de julio" podía pedirse como 30 de junio).
const iso = isoLocal;

/** Atajos calculados sobre la fecha de hoy; ninguno inventa datos. */
const buildPresets = () => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = hoy.getMonth();

  const finDeMes = (year, month) => new Date(year, month + 1, 0);

  return [
    {
      id: 'este-mes',
      label: 'Este mes',
      start: iso(new Date(y, m, 1)),
      end: iso(hoy),
    },
    {
      id: 'mes-pasado',
      label: 'Mes pasado',
      start: iso(new Date(y, m - 1, 1)),
      end: iso(finDeMes(y, m - 1)),
    },
    {
      id: 'tres-meses',
      label: 'Últimos 3 meses',
      start: iso(new Date(y, m - 2, 1)),
      end: iso(hoy),
    },
    {
      id: 'este-anio',
      label: 'Este año',
      start: iso(new Date(y, 0, 1)),
      end: iso(hoy),
    },
    {
      id: 'anio-pasado',
      label: 'Año pasado',
      start: iso(new Date(y - 1, 0, 1)),
      end: iso(new Date(y - 1, 11, 31)),
    },
  ];
};

const fmt = (value) => {
  if (!value) return '—';
  const [yy, mm, dd] = value.split('-');
  return `${dd}/${mm}/${yy}`;
};

const PeriodPicker = ({ dateRange, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [alineacion, setAlineacion] = useState('left');
  const [altoMaximo, setAltoMaximo] = useState(560);
  const disparador = useRef(null);
  const presets = buildPresets();

  // Dos meses caben en escritorio y hacen mucho más cómodo elegir un rango que
  // cruza de mes; en un teléfono solo cabe uno, y los atajos pasan de columna
  // lateral a fila superior.
  const estrecho = typeof window !== 'undefined' && window.innerWidth < 760;
  const mesesVisibles = estrecho ? 1 : 2;

  const activePreset = presets.find(
    (p) => p.start === dateRange?.startDate && p.end === dateRange?.endDate
  );

  const pick = (preset) => {
    onChange({ startDate: preset.start, endDate: preset.end });
    setOpen(false);
  };

  /**
   * El desplegable es ancho (dos meses) y este control cambia de sitio según el
   * ancho de la ventana: a veces queda a la izquierda de la tarjeta y a veces
   * pegado al borde derecho. Al abrirlo se mide si cabe hacia la derecha; si no,
   * se ancla por el otro lado. Sin esto se salía de la pantalla.
   */
  const alternar = () => {
    if (!open) {
      const caja = disparador.current?.getBoundingClientRect();
      const ancho = estrecho ? 320 : 620;
      const cabeALaDerecha = !caja || caja.left + ancho < window.innerWidth - 12;
      setAlineacion(cabeALaDerecha ? 'left' : 'right');

      // Alto disponible bajo el disparador. En un teléfono hay que descontar
      // además la barra inferior de navegación, que flota sobre el contenido.
      if (caja) {
        const margen = estrecho ? 96 : 24;
        setAltoMaximo(Math.max(300, Math.round(window.innerHeight - caja.bottom - margen)));
      }
    }
    setOpen((v) => !v);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ font: "600 .74rem 'Hanken Grotesk'", color: 'var(--muted)', marginBottom: 5 }}>
        Periodo a descargar
      </div>

      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface-2)',
          border: '1px solid var(--line-2)', borderRadius: 10, padding: '10px 14px',
          cursor: disabled ? 'not-allowed' : 'pointer', color: 'var(--heading)',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <Sym name="calendar_month" size={20} style={{ color: 'var(--brand-2)' }} />
        <span className="a2-mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '.9rem', fontWeight: 600 }}>
          {fmt(dateRange?.startDate)}
          <span style={{ color: 'var(--muted)' }}>→</span>
          {fmt(dateRange?.endDate)}
        </span>
        <Sym name="expand_more" size={19} style={{ color: 'var(--muted)' }} />
      </button>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        align={alineacion}
        width={estrecho ? 320 : 620}
        style={{ maxHeight: altoMaximo, overflowY: 'auto' }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: estrecho ? '1fr' : '190px 1fr',
            gap: estrecho ? 4 : 10,
          }}
        >
          {/* Atajos: columna a la izquierda en escritorio, fila arriba en móvil */}
          <div style={estrecho ? undefined : { borderRight: '1px solid var(--line)', paddingRight: 6 }}>
            <MenuLabel>Periodos frecuentes</MenuLabel>
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                role="menuitem"
                className="as2-menu-item"
                onClick={() => pick(p)}
                style={activePreset?.id === p.id ? { background: 'var(--brand-soft)' } : undefined}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', font: "700 .86rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
                    {p.label}
                  </span>
                  <span className="a2-mono" style={{ display: 'block', fontSize: '.72rem', color: 'var(--muted)' }}>
                    {fmt(p.start)} → {fmt(p.end)}
                  </span>
                </span>
                {activePreset?.id === p.id ? (
                  <Sym name="check" size={18} style={{ color: 'var(--brand-2)' }} />
                ) : null}
              </button>
            ))}
          </div>

          {/* Calendario */}
          <div style={{ padding: estrecho ? '4px 6px 2px' : '4px 6px 2px', minWidth: 0 }}>
            <MenuLabel>Periodo personalizado</MenuLabel>

            <RangeCalendar
              start={dateRange?.startDate}
              end={dateRange?.endDate}
              onChange={onChange}
              months={mesesVisibles}
            />
          </div>
        </div>

        <div className="as2-menu-sep" />

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 4px',
            position: 'sticky', bottom: -8, background: 'var(--surface)',
          }}
        >
          <span className="a2-mono" style={{ flex: 1, minWidth: 0, fontSize: '.82rem', color: 'var(--heading)' }}>
            {fmt(dateRange?.startDate)} <span style={{ color: 'var(--muted)' }}>→</span> {fmt(dateRange?.endDate)}
          </span>
          <Button
            size="sm"
            icon="check"
            onClick={() => setOpen(false)}
            disabled={!dateRange?.startDate || !dateRange?.endDate}
          >
            Aplicar periodo
          </Button>
        </div>
      </Menu>
    </div>
  );
};

export default PeriodPicker;
